import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADE_LEVELS = [
  { value: "light",  label: "Light grade"  },
  { value: "medium", label: "Medium grade" },
  { value: "heavy",  label: "Heavy grade"  },
  { value: "none",   label: "No grade · raw" },
];

function fullUrl(u) {
  if (!u) return u;
  if (u.startsWith("/api/uploads/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function AdminMediaTab() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [grade, setGrade] = useState("medium");
  const [watermark, setWatermark] = useState(true);

  const fetchMedia = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/media`, { withCredentials: true });
      setMedia(r.data || []);
    } catch (e) {
      toast.error("Could not load media");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("grade_level", grade);
        fd.append("watermark", String(watermark));
        await axios.post(`${API}/admin/media/upload`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} file(s) uploaded + graded`);
      fetchMedia();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this media file? It may break content currently using it.")) return;
    await axios.delete(`${API}/admin/media/${id}`, { withCredentials: true });
    toast.success("Deleted");
    fetchMedia();
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div data-testid="admin-media-tab">
      <div className="flex flex-wrap items-end gap-4 mb-8 border border-white/10 p-5">
        <div className="flex-1 min-w-[200px]">
          <p className="overline mb-2">Cinematic grade</p>
          <div className="flex flex-wrap gap-2">
            {GRADE_LEVELS.map((g) => (
              <button
                key={g.value}
                data-testid={`grade-${g.value}`}
                onClick={() => setGrade(g.value)}
                className={`font-mono text-[10px] tracking-[0.25em] uppercase border px-3 py-2 transition-all ${
                  grade === g.value
                    ? "border-[#c01515] text-[#c01515] bg-[#c01515]/10"
                    : "border-white/15 text-[#a3a3a3] hover:text-white hover:border-white/40"
                }`}
              >{g.label}</button>
            ))}
          </div>
        </div>
        <div>
          <p className="overline mb-2">Watermark</p>
          <button
            data-testid="watermark-toggle"
            onClick={() => setWatermark((v) => !v)}
            className={`font-mono text-[10px] tracking-[0.25em] uppercase border px-3 py-2 transition-all ${
              watermark
                ? "border-[#c01515] text-[#c01515] bg-[#c01515]/10"
                : "border-white/15 text-[#a3a3a3]"
            }`}
          >{watermark ? "● Watermark on" : "○ Off"}</button>
        </div>
        <label
          className="font-mono text-[10px] tracking-[0.25em] uppercase border border-[#c01515] bg-[#c01515] text-white px-4 py-3 hover:opacity-90 transition-all cursor-pointer flex items-center gap-2"
          data-testid="upload-label"
        >
          <Upload size={14} /> {uploading ? "Uploading…" : "Upload images"}
          <input
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
            data-testid="media-upload-input"
          />
        </label>
      </div>

      {loading ? (
        <div className="overline p-10 text-center" data-testid="media-loading">Loading library…</div>
      ) : media.length === 0 ? (
        <div className="overline p-10 text-center border border-white/10" data-testid="media-empty">
          No uploaded media yet. Drop your first image above.
        </div>
      ) : (
        <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
          {media.map((m) => (
            <li key={m.id} className="border border-white/10 group" data-testid={`media-${m.id}`}>
              <div className="aspect-[4/5] bg-[#0a0a0a] overflow-hidden">
                <img src={fullUrl(m.url)} alt={m.filename} loading="lazy"
                     className="w-full h-full object-cover" />
              </div>
              <div className="p-3 space-y-2">
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#a3a3a3] truncate">
                  {m.filename}
                </p>
                <p className="font-mono text-[9px] tracking-[0.2em] uppercase opacity-70">
                  {m.width}×{m.height} · {fmtSize(m.size_bytes)} · {m.grade}
                </p>
                <div className="flex items-center gap-2 pt-2">
                  <button
                    data-testid={`copy-${m.id}`}
                    onClick={() => handleCopy(m.url)}
                    className="flex-1 font-mono text-[9px] tracking-[0.25em] uppercase border border-white/20 px-2 py-1.5 hover:bg-white hover:text-black transition-all flex items-center justify-center gap-1"
                  ><Copy size={10} /> URL</button>
                  <button
                    data-testid={`delete-${m.id}`}
                    onClick={() => handleDelete(m.id)}
                    className="font-mono text-[9px] tracking-[0.25em] uppercase border border-[#c01515]/40 text-[#c01515] px-2 py-1.5 hover:bg-[#c01515] hover:text-white transition-all"
                  ><Trash2 size={10} /></button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
